import { ApolloClient, ApolloLink, InMemoryCache, Observable } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import Cookies from 'js-cookie';
import { notification } from 'antd';

const AUTH_ERROR_MESSAGES = [
  'your account is not yet activated',
  'your account is inactive',
];

const isAuthError = (message: string) =>
  AUTH_ERROR_MESSAGES.some((msg) => message.toLowerCase().includes(msg));

const httpLink = createUploadLink({
  uri: process.env.REACT_APP_GRAPHQL_ENDPOINT,
});

const authLink = setContext((_, { headers }) => {
  const csrfToken = 'key';
  const token = Cookies.get('auth_token');
  return {
    headers: {
      ...headers,
      'X-CSRF-Token': csrfToken,
      authorization: headers?.authorization || headers?.Authorization || (token ? `Bearer ${token}` : '')
    },
  };
});

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message }) => {
      if (isAuthError(message)) {
        Cookies.remove('user');
        Cookies.remove('auth_token');
        // Store error message for the login page to display
        sessionStorage.setItem('auth_redirect_message', message);
        window.location.href = '/signin';
      }
    });
  }
});

const readOnlyLink = new ApolloLink((operation, forward) => {
  const userCookie = Cookies.get('user');
  let isReadOnly = false;
  try {
    if (userCookie) {
      const user = JSON.parse(userCookie);
      isReadOnly = !!user.isReadOnly;
    }
  } catch (e) {
    console.error('Error parsing user cookie in readOnlyLink', e);
  }

  const definitions = operation.query.definitions;
  const isMutation = definitions.some(
    (def) => def.kind === 'OperationDefinition' && def.operation === 'mutation'
  );

  if (isReadOnly && isMutation) {
    const isLogout = operation.operationName === 'adminSignout' || operation.operationName === 'userSignout'; // Assuming these names based on typical patterns
    
    if (!isLogout) {
      notification.warning({
        message: 'Read-only Mode',
        description: 'Action disabled while viewing as professional.',
        placement: 'topRight',
      });
      return new Observable((observer) => {
        observer.next({ data: null, errors: [{ message: 'Action disabled in read-only mode' } as any] });
        observer.complete();
      });
    }
  }

  return forward(operation);
});

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, readOnlyLink, authLink.concat(httpLink as any)]),
  cache: new InMemoryCache(),
});

export default client;
